import type { Team } from './domain.js';
import type { ActivityCoverage, BoardActivity } from './boardCoverage.js';
import { timeToMinutes } from './boardCoverage.js';

// Day-Gantt geometry (S4 UI pass 2026-07-17): pure helpers behind the
// hour-axis chart on bos/reception — team grouping, percent-positioned
// bars, hour ticks, and the stable employee colour/initials chips. No React,
// no DOM: the apps copy only a thin rendering component.

/** Gantt row groups in display order — team-grouped, unassigned last. */
export interface GanttGroup {
  team: Team | null;
  items: ActivityCoverage[];
}

/**
 * The one team order, everywhere (operator, 2026-08-06): Ménage, then
 * Réception/Salle, then Cuisine, unassigned last. It follows the working day —
 * the rooms are done first, the desk runs through it, the kitchen closes it —
 * and it is the same sequence on every page (Équipe, Activités, Pointages, the
 * board), so a reader never has to re-learn where a team sits.
 *
 * `TEAMS` in the admin app is this list without the null.
 */
export const TEAM_ORDER: (Team | null)[] = ['menage', 'receptionSalle', 'cuisine', null];

export function groupCoverageByTeam(coverages: ActivityCoverage[]): GanttGroup[] {
  return TEAM_ORDER.map((team) => ({
    team,
    items: coverages.filter((c) => c.activity.team === team),
  })).filter((g) => g.items.length > 0);
}

/**
 * Flat day-Gantt order (2026-07-19): every activity in one chronological list
 * by effective window start, then end, then label — team is conveyed by colour
 * (bar + font), not by grouping. Ties broken deterministically so the order is
 * stable across renders.
 */
export function sortCoverageByStart(coverages: ActivityCoverage[]): ActivityCoverage[] {
  return [...coverages].sort(
    (a, b) =>
      timeToMinutes(a.windowStart) - timeToMinutes(b.windowStart) ||
      timeToMinutes(a.windowEnd) - timeToMinutes(b.windowEnd) ||
      a.activity.label.localeCompare(b.activity.label, 'fr'),
  );
}

/** The teams present in a coverage set, in display order — drives the Gantt colour legend. */
export function teamsInCoverage(coverages: ActivityCoverage[]): (Team | null)[] {
  return TEAM_ORDER.filter((t) => coverages.some((c) => c.activity.team === t));
}

/**
 * Same chronological order as sortCoverageByStart, for a flat activity list —
 * the week board's ONE row per activity (no per-day override context; the
 * activity's own default window is the ordering key, matching the window
 * label already shown on that row).
 */
export function sortActivitiesByStart(activities: BoardActivity[]): BoardActivity[] {
  return [...activities].sort(
    (a, b) =>
      timeToMinutes(a.windowStart) - timeToMinutes(b.windowStart) ||
      timeToMinutes(a.windowEnd) - timeToMinutes(b.windowEnd) ||
      a.label.localeCompare(b.label, 'fr'),
  );
}

/** The teams present in an activity list, in display order — the week board's colour legend. */
export function teamsInActivities(activities: BoardActivity[]): (Team | null)[] {
  return TEAM_ORDER.filter((t) => activities.some((a) => a.team === t));
}

export interface GanttRange {
  /** Minutes from midnight, floored/ceiled to the hour. */
  startMin: number;
  endMin: number;
}

/** Hour-aligned range covering every bar (effective windows), with a fallback working day. */
export function ganttRange(coverages: ActivityCoverage[]): GanttRange {
  if (coverages.length === 0) return { startMin: 6 * 60, endMin: 23 * 60 };
  let start = Infinity;
  let end = -Infinity;
  for (const c of coverages) {
    start = Math.min(start, timeToMinutes(c.windowStart));
    end = Math.max(end, timeToMinutes(c.windowEnd));
  }
  return {
    startMin: Math.floor(start / 60) * 60,
    endMin: Math.ceil(end / 60) * 60,
  };
}

/**
 * End-minutes of a window that may cross midnight (2026-08-06): a clock-out at
 * or before the clock-in belongs to the NEXT day — 18:57 → 00:43 is a 5 h 46
 * shift, not a negative one. Returns minutes past the START day's midnight, so
 * it can exceed 1440 and stays comparable with the start.
 */
export function endMinutesCrossingMidnight(startMin: number, endMin: number): number {
  return endMin <= startMin ? endMin + 24 * 60 : endMin;
}

/** Widen a range so it also covers [startMin, endMin], keeping hour alignment. */
export function extendGanttRange(range: GanttRange, startMin: number, endMin: number): GanttRange {
  return {
    startMin: Math.min(range.startMin, Math.floor(startMin / 60) * 60),
    endMin: Math.max(range.endMin, Math.ceil(endMin / 60) * 60),
  };
}

/** Percent geometry of an arbitrary [startMin, endMin] window inside the range (clamped). */
export function ganttBarFromMinutes(
  startMin: number,
  endMin: number,
  range: GanttRange,
): { leftPct: number; widthPct: number } {
  const span = Math.max(1, range.endMin - range.startMin);
  const start = Math.max(range.startMin, startMin);
  const end = Math.min(range.endMin, endMin);
  return {
    leftPct: ((start - range.startMin) / span) * 100,
    widthPct: (Math.max(0, end - start) / span) * 100,
  };
}

/** Percent geometry of one activity bar inside the range. */
export function ganttBar(c: ActivityCoverage, range: GanttRange): { leftPct: number; widthPct: number } {
  return ganttBarFromMinutes(timeToMinutes(c.windowStart), timeToMinutes(c.windowEnd), range);
}

/** Hour tick marks: label ('06 h') + left percent, one per full hour. */
export function ganttTicks(range: GanttRange): { label: string; leftPct: number }[] {
  const span = Math.max(1, range.endMin - range.startMin);
  const ticks = [];
  for (let m = range.startMin; m <= range.endMin; m += 60) {
    ticks.push({
      // % 24 so a range spilling past midnight labels 00 h / 01 h, not 24 h / 25 h.
      label: `${String(Math.floor(m / 60) % 24).padStart(2, '0')} h`,
      leftPct: ((m - range.startMin) / span) * 100,
    });
  }
  return ticks;
}

/** 'Quentin' + 'SALVAT' → 'QS' — the chip label that keeps bars narrow. */
export function employeeInitials(firstName: string, lastName: string): string {
  return `${firstName.trim().charAt(0)}${lastName.trim().charAt(0)}`.toUpperCase();
}

/** Gantt/board chip label: the nickname when set, else the initials fallback. */
export function chipLabel(e: { nickname?: string | null; firstName: string; lastName: string }): string {
  return e.nickname || employeeInitials(e.firstName, e.lastName);
}

/**
 * Fixed 12-hue chip palette (WCAG-friendly on white). Assignment is by
 * SORTED id, so a given employee keeps the same colour on every surface and
 * every render regardless of fetch order.
 */
export const EMPLOYEE_COLORS = [
  '#1f6b86',
  '#a55a14',
  '#6b3a9c',
  '#2f7d3b',
  '#a5341f',
  '#8a7a55',
  '#2a3957',
  '#b3487d',
  '#3f7f7f',
  '#7a6210',
  '#556b2f',
  '#5a5a8a',
] as const;

export function assignEmployeeColors(ids: Iterable<string>): Map<string, string> {
  const sorted = [...new Set(ids)].sort();
  return new Map(sorted.map((id, i) => [id, EMPLOYEE_COLORS[i % EMPLOYEE_COLORS.length] as string]));
}
